import type { ServiceConfigsValues } from '../../common/constants'
import { InvalidConfigError } from '../../errors'
import { Params } from './params'
import type { SchemaSource } from './types'

// The extension's `configItems` slot: its description for the console, and the resolution of a concrete
// invocation's values.
//
// Resolution runs the shared normalize → coerce → validate pipeline (see ./params.ts and ./schema/normalize),
// so `configItems` obeys exactly the same policy as every other declared slot. It replaces the v1
// `prepareServiceConfig` for this format: that resolver routed by the `shared` flag and applied defaults by
// truthiness, which silently dropped a falsy default and never type-checked anything.

export class ConfigParams extends Params {
  static of(scheme?: unknown, source: SchemaSource = {}): ConfigParams {
    return new ConfigParams(scheme, source)
  }

  static empty(): ConfigParams {
    return new ConfigParams(undefined)
  }

  /**
   * The effective config for one invocation.
   *
   * Fails with every offending field at once rather than the first, so a half-configured service reports its
   * whole form. A field the schema resolved to "not present" is simply absent from the result.
   */
  resolve(configs?: ServiceConfigsValues): ServiceConfigsValues {
    const { values, issues } = this.normalizeInput(configs)

    if (issues.length) {
      throw new InvalidConfigError(this.source.serviceId ?? 'unknown', issues)
    }

    return values
  }
}
